import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import InvoiceRow from '@/components/InvoiceRow';
import EmptyState from '@/components/EmptyState';
import InvoiceDetailModal from '@/components/modals/InvoiceDetailModal';
import { I } from '@/lib/icons';
import { useWorkspace } from '@/hooks/useData';
import { useInvoicesScoped } from '@/hooks/useInvoicesAcrossWorkspaces';
import { useToast } from '@/components/Toast';
import { isOverdue } from '@/lib/format';

const SCOPE_KEY = 'cashdeck-faktury-scope-v1';

export default function Faktury() {
  const navigate = useNavigate();
  const toast = useToast();
  const { workspace, list: workspaces, switchWorkspace } = useWorkspace();
  const [scope, setScope] = useState(() => {
    try { return localStorage.getItem(SCOPE_KEY) || 'current'; } catch (_e) { return 'current'; }
  });
  useEffect(() => {
    try { localStorage.setItem(SCOPE_KEY, scope); } catch (_e) {}
  }, [scope]);

  const effectiveWsId = scope === 'ALL' ? 'ALL' : (scope === 'current' ? (workspace?.id || null) : scope);
  const { invoices, loading, update } = useInvoicesScoped(effectiveWsId);

  const [filter, setFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [detail, setDetail] = useState(null);
  const [scopeOpen, setScopeOpen] = useState(false);
  const scopeWrapRef = useRef(null);

  useEffect(() => {
    if (!scopeOpen) return;
    const onClick = (e) => { if (scopeWrapRef.current && !scopeWrapRef.current.contains(e.target)) setScopeOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setScopeOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [scopeOpen]);

  const years = Array.from(new Set(invoices.map(i => new Date(i.issue_date).getFullYear()))).sort((a, b) => b - a);

  let listRows = invoices;
  if (yearFilter !== 'all') listRows = listRows.filter(i => new Date(i.issue_date).getFullYear() === Number(yearFilter));
  if (filter !== 'all') {
    if (filter === 'overdue') listRows = listRows.filter(i => isOverdue(i));
    else listRows = listRows.filter(i => i.status === filter);
  }

  const onMarkPaid = async (invoice) => {
    try {
      await update(invoice.id, { status: 'paid', paid_at: new Date().toISOString().slice(0, 10) });
      toast.success(`Faktúra ${invoice.number} označená ako zaplatená.`);
    } catch (e) { toast.error(e.message); }
  };

  const handleRowClick = (inv) => {
    if (effectiveWsId === 'ALL' && inv.workspace_id && inv.workspace_id !== workspace?.id) {
      switchWorkspace(inv.workspace_id);
    }
    setDetail(inv);
  };

  const scopeOptions = useMemo(() => {
    const base = [
      { id: 'current', label: workspace?.name ? `${workspace.name} (aktuálna)` : 'Aktuálna firma' },
      { id: 'ALL', label: 'Všetky firmy' },
    ];
    const perFirma = (workspaces || [])
      .filter(w => w.id !== workspace?.id)
      .map(w => ({ id: w.id, label: w.name }));
    return [...base, ...perFirma];
  }, [workspace, workspaces]);

  const selectedLabel = useMemo(() => {
    const found = scopeOptions.find(o => o.id === scope);
    return found?.label || 'Firma';
  }, [scopeOptions, scope]);

  const meta = effectiveWsId === 'ALL'
    ? `${invoices.length} naprieč firmami`
    : `${invoices.length} celkom`;

  return (
    <>
      <PageHeader title="Faktúry" meta={meta} accent="blue"
        right={<button onClick={() => navigate('/faktury/nova')} className="cd-btn cd-btn-primary" data-testid="new-invoice-btn"><I.plus /> Nová faktúra</button>} />

      {/* Scope dropdown + filters */}
      <div style={{ padding: '6px 32px 16px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div ref={scopeWrapRef} style={{ position: 'relative' }}>
          <button
            type="button"
            data-testid="faktury-scope-trigger"
            onClick={() => setScopeOpen(o => !o)}
            aria-haspopup="listbox"
            aria-expanded={scopeOpen}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 10px', borderRadius: 'var(--r-md)',
              border: '1px solid var(--border)', background: 'var(--bg-surface)',
              cursor: 'pointer', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
              color: 'var(--text-primary)',
            }}
          >
            <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Firma:</span>
            <span>{selectedLabel}</span>
            <I.chevD />
          </button>
          {scopeOpen && (
            <div role="listbox" style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0,
              minWidth: 220, background: 'var(--bg-surface)',
              border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50,
              overflow: 'hidden',
            }}>
              {scopeOptions.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`faktury-scope-${opt.id}`}
                  onClick={() => { setScope(opt.id); setScopeOpen(false); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '8px 12px', background: scope === opt.id ? 'var(--bg-hover)' : 'transparent',
                    border: 'none', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
                    color: 'var(--text-primary)',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ width: 1, background: 'var(--border)', margin: '0 4px', alignSelf: 'stretch' }} />

        {[
          { id: 'all', label: 'Všetky' },
          { id: 'issued', label: 'Vystavené' },
          { id: 'paid', label: 'Zaplatené' },
          { id: 'overdue', label: 'Po splatnosti' },
          { id: 'draft', label: 'Koncepty' },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`filter-${f.id}`}
            className={`cd-chip${filter === f.id ? ' active' : ''}`}>{f.label}</button>
        ))}
        {years.length > 1 && (
          <>
            <div style={{ width: 1, background: 'var(--border)', margin: '0 4px' }} />
            <button onClick={() => setYearFilter('all')} className={`cd-chip${yearFilter === 'all' ? ' active' : ''}`}>Všetky roky</button>
            {years.map(y => (
              <button key={y} onClick={() => setYearFilter(String(y))} className={`cd-chip${yearFilter === String(y) ? ' active' : ''}`}>{y}</button>
            ))}
          </>
        )}
      </div>

      {/* List */}
      <div style={{ padding: '0 32px 32px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            <span className="cd-spinner" /> Načítavam...
          </div>
        ) : listRows.length === 0 ? (
          <EmptyState icon="doc" color="blue"
            title={invoices.length === 0 ? 'Zatiaľ žiadne faktúry' : 'Nič v tomto filtri'}
            desc={invoices.length === 0
              ? 'Vystav prvú faktúru — PDF vygenerujeme za teba.'
              : 'Skús iný filter alebo vytvor novú faktúru.'}
            ctaLabel="Nová faktúra" onCta={() => navigate('/faktury/nova')} />
        ) : (
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-lg)', overflow: 'hidden',
          }}>
            {listRows.map((inv, i) => (
              <InvoiceRow
                key={inv.id}
                invoice={inv}
                divider={i < listRows.length - 1}
                onClick={() => handleRowClick(inv)}
                onMarkPaid={onMarkPaid}
                workspaceLabel={effectiveWsId === 'ALL' ? (inv.workspace?.name || '') : undefined}
              />
            ))}
          </div>
        )}
      </div>

      <InvoiceDetailModal
        open={!!detail}
        invoice={detail}
        onClose={() => setDetail(null)}
      />
    </>
  );
}
